import {
  ReactElement, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import SelectSearch, { fuzzySearch } from 'react-select-search';
import { toast } from 'react-toastify';
import { Box, Flex } from 'reflexbox';
import {
  ActionButton,
  Button,
  Checkbox,
  Loader,
  ModalWindow,
} from '~/components';
import { dynamicSort, Pagination } from '~/components/NewTable';
import { getObjects } from '~/helpers/getObjetcs';
import { getUserProfile } from '~/helpers/userProfile';
import { useStateVar } from '~/helpers/useStateVar';
import { DeleteOutlineIcon, EditIcon } from '~/icons';
import { SimpleButton } from '~/pages/Admin/AdmCities/styles';
import { Label, SearchInput } from '~/pages/Analysis/styles';
import { apiCall, ApiResps } from '~/providers';
import { colors } from '~/styles/colors';
import { InputSearch } from '../../../components/InputSearch';
import { EnvironmentList } from '../ClientPanel';
import { canEditRegistry, canManageWholePanel } from '../helpers/registryEdit';
import {
  ChevronBottom,
  ChevronTop,
  DataCell,
  HeaderCell,
  HeaderRow,
  Row,
  TableBody,
  TableContainer,
  TableHead,
} from './styles';

interface TableDutsProps {
  list: EnvironmentList[],
  vavsList: ApiResps['/dri/get-dri-vavs-list']['list'],
  roomTypes: { value: number, name: string}[],
  pageSize: number,
  onEditClick: (itemToEdit?: EnvironmentList) => void,
  onDeleteClick: (item: EnvironmentList) => void,
  render: () => void,
  setIsLoad: (isLoad: boolean) => void,
  // Cliente do Painel: no Painel do Admin de cliente não há cliente na rota.
  clientId?: number,
}

export const TableDuts = ({
  list,
  vavsList,
  onEditClick,
  onDeleteClick,
  roomTypes,
  render,
  pageSize,
  setIsLoad,
  clientId: panelClientId,
}: Readonly<TableDutsProps>): JSX.Element => {
  const rows = useMemo(() => list.concat(vavsList as unknown as typeof list), [list, vavsList]);
  const [profile] = useState(getUserProfile);
  const canManage = canManageWholePanel(profile, panelClientId);
  const canEditEnvironments = canEditRegistry(profile, panelClientId);
  const [state, _renderVar, setState] = useStateVar(() => {
    const state = {
      selectOption: '' as any,
      searchText: '',
      currentPage: 1,
      sortOrder: '+',
      orderColum: '',
      isLoading: false,
      numSelect: 0,
      selectedRType: undefined,
    };
    return state;
  });
  const [filteredRows, setFilteredRows] = useState(rows);
  const [listEnvironments, setListEnvironments] = useState<{ DEV_ID: string, ENV_ID: number }[]>([]);
  const { t } = useTranslation();
  const routeParams = useParams<{ clientId: string }>();
  function getPages() {
    return filteredRows.map((item, index) => ({ ...item, key: `${index} ${item.DEV_ID} ${item.ENVIRONMENT_ID}` })).slice((state.currentPage - 1) * pageSize, state.currentPage * pageSize);
  }

  async function verifyRoute(arrayPromisse, clientId, type) {
    if (state.selectOption === 'deleteEnvironments') {
      await Promise.all(
        arrayPromisse.map((arr) => apiCall('/dut/delete-environments', { DEVS_INFO: arr })),
      ).then(() => {
        toast.success(t('ambientesDeletadosComSucesso'));
        toast.info(t('dutsNaoForamDeletados'));
        setListEnvironments([]);
        setState({ selectOption: '', selectedRType: undefined });
        render();
      }).catch((err) => {
        console.log(err);
        toast.error(t('houveErro'));
        setState({ selectOption: '' });
      });
    } else {
      await Promise.all(
        arrayPromisse.map((arr) => apiCall('/dut/set-dut-rtype-v2', { DEVS_INFO: arr, RTYPE_ID: type === 'clear' ? null : state.selectedRType })),
      ).then(() => {
        toast.success(t('tipoAmbientesAlteradoSucesso'));
        toast.info(t('ambientesSemNomeDefinidoNaoForamAlterados'));
        setListEnvironments([]);
        setState({ selectOption: '', selectedRType: undefined });
        render();
      }).catch((err) => {
        console.log(err);
        toast.error(t('houveErro'));
        setState({ selectOption: '' });
      });
    }
  }

  async function updateOrDelete(type) {
    if (listEnvironments.length > 0) {
      if (state.selectOption !== 'deleteEnvironments' && type !== 'clear' && !state.selectedRType) {
        toast.error(t('selecioneOTipoDeAmbiente'));
        return;
      }
      const arrayPromisse: { DEV_ID: string, ENV_ID: number }[][] = [];
      const clientId = (routeParams.clientId && Number(routeParams.clientId)) || null;
      for (let i = 0; i < listEnvironments.length; i += 100) {
        arrayPromisse.push(listEnvironments.slice(i, i + 100));
      }
      await verifyRoute(arrayPromisse, clientId, type);
      setState({ isLoading: true });
    } else {
      toast.error(t('deveSelecionarPeloMenos1Ambiente'));
      setState({ selectOption: '', selectedRType: undefined });
    }
    setState({ isLoading: false });
  }

  const getDataPerPage = useMemo(() => getPages(), [filteredRows, state.currentPage, pageSize]);
  useEffect(() => {
    setFilteredRows(rows);
    setState({ currentPage: 1 });
  }, [list, vavsList]);

  useEffect(() => {
    setState({ numSelect: listEnvironments.length });
  }, [listEnvironments]);

  const onSearch = (e) => {
    setState({ searchText: e.target.value });

    if (e.target.value) {
      setFilteredRows(rows.filter((row) => getObjects(row, e.target.value).length));
    } else {
      setFilteredRows(rows);
    }

    setState({ orderColum: 'name' });
    setState({ sortOrder: '+' });
    setState({ currentPage: 1 });
  };

  const columnsEnvironment = [
    {
      label: t('nome'),
      value: 'name',
      accessor: 'ROOM_NAME',
    },
    {
      label: t('tipo'),
      value: 'type',
      accessor: 'RTYPE_NAME',
    },
    {
      label: t('unidade'),
      value: 'unit',
      accessor: 'UNIT_NAME',
    },
    {
      label: t('pais'),
      value: 'country',
      accessor: 'COUNTRY_NAME',
    },
    {
      label: t('estado'),
      value: 'state',
      accessor: 'STATE_ID',
    },
    {
      label: t('cidade'),
      value: 'city',
      accessor: 'CITY_NAME',
    },
  ];

  const handleSetOrderColumn = (column) => {
    let newSortOrder = '+';
    if (state.orderColum === column.value && state.sortOrder === '+') {
      newSortOrder = '-';
    }
    let sortedRows;
    if (state.orderColum === column.value && state.sortOrder === newSortOrder) {
      setState({ orderColum: '' });
      setState({ sortOrder: '' });
    } else {
      sortedRows = [...filteredRows].sort(dynamicSort(column, newSortOrder));
      setState({ orderColum: column.value });
      setState({ sortOrder: newSortOrder });
    }

    if (state.currentPage !== 1) {
      setState({ currentPage: 1 });
    }

    setFilteredRows(sortedRows);
  };

  const chevron = () => {
    if (state.sortOrder === '+') {
      return <ChevronTop />;
    }
    return <ChevronBottom />;
  };

  function checkItem(item) {
    const newItem = item.DEV_ID !== '-' ? 'DEV_ID' : 'ENV_ID';
    const value = newItem === 'ENV_ID' ? item.ENVIRONMENT_ID : item.DEV_ID;
    if (listEnvironments.some((dut) => dut[newItem] === value)) {
      setListEnvironments(listEnvironments.filter((dev) => dev[newItem] !== value));
      item.checked = false;
    } else {
      item.checked = true;
      setListEnvironments([...listEnvironments, {
        DEV_ID: item.DEV_ID,
        ENV_ID: item.ENVIRONMENT_ID,
      }]);
    }
  }
  function checkAllItems() {
    setFilteredRows(filteredRows.map((item) => ({ ...item, checked: true })));
    const notIncludes = filteredRows.filter((item) => {
      const key = item.DEV_ID !== '-' ? 'DEV_ID' : 'ENV_ID';
      const value = key === 'ENV_ID' ? item.ENVIRONMENT_ID : item.DEV_ID;
      return !listEnvironments.some((dut) => dut[key] === value);
    });
    setListEnvironments([...listEnvironments, ...notIncludes.map((item) => ({
      DEV_ID: item.DEV_ID,
      ENV_ID: item.ENVIRONMENT_ID,
    }))]);
  }

  function notCheckAllItems() {
    setFilteredRows(filteredRows.map((item) => ({ ...item, checked: false })));
    const includes = listEnvironments.filter((item) => {
      const key = item.DEV_ID !== '-' ? 'DEV_ID' : 'ENVIRONMENT_ID';
      const value = key === 'ENVIRONMENT_ID' ? item.ENV_ID : item.DEV_ID;
      return !filteredRows.some((filterItem) => filterItem[key] === value);
    });
    setListEnvironments([...includes]);
  }

  const onPreviousPage = () => {
    if (state.currentPage > 1) {
      setState({ currentPage: state.currentPage - 1 });
    }
  };

  const onNextPage = () => {
    if (state.currentPage * pageSize < filteredRows.length) {
      setState({ currentPage: state.currentPage + 1 });
    }
  };

  function isChecked() {
    return !filteredRows.some((item) => {
      const key = item.DEV_ID !== '-' ? 'DEV_ID' : 'ENV_ID';
      const value = key === 'ENV_ID' ? item.ENVIRONMENT_ID : item.DEV_ID;
      return !listEnvironments.some((dev) => dev[key] === value);
    });
  }

  function isCheckedItem(item) {
    const key = item.DEV_ID !== '-' ? 'DEV_ID' : 'ENV_ID';
    const value = key === 'ENV_ID' ? item.ENVIRONMENT_ID : item.DEV_ID;
    return listEnvironments.some((dev) => dev[key] === value);
  }

  if (state.isLoading) {
    return <Loader />;
  }

  return (
    <>
      {canManage && (
        <Flex flexWrap="wrap" flexDirection="row" width="100%" justifyContent="flex-start" marginBottom="5px" style={{ gap: 8 }}>
          <SimpleButton variant="primary" onClick={() => onEditClick()}>{t('adicionarAmbiente')}</SimpleButton>
          {rows.length > 0 && (
            <SimpleButton variant="primary" onClick={() => { setState({ selectOption: 'deleteEnvironments' }); }}>{t('excluirAmbientes')}</SimpleButton>
          )}
        </Flex>
      )}
      {rows.length === 0 && <div>{`(${t('nenhum')})`}</div>}
      {rows.length > 0 && (
        <>
          <Flex flexWrap="wrap" flexDirection="row" width="100%" justifyContent="space-between" style={{ gap: 10 }}>
            <Box minWidth="200px" width={[1, 1, 1, 1, 1 / 5]} mb={[16, 16, 16, 16, 16, 0]} style={{ margin: 0 }}>
              <InputSearch
                id="searchDuts"
                name="search"
                placeholder={t('pesquisar')}
                value={state.searchText}
                onChange={onSearch}
                style={{
                  margin: 0,
                }}
              />
            </Box>
            {
              canEditEnvironments && (
                <SearchInput style={{ width: 300, margin: 0 }}>
                  <div
                    style={{
                      width: '100%',
                      paddingTop: 1,
                      paddingBottom: 3,
                      margin: 0,
                    }}
                  >
                    <Label>{t('multiplasConfiguracoes')}</Label>
                    <SelectSearch
                      options={[
                        {
                          value: 'setEnvironmentType',
                          name: t('definirTipoAmbiente'),
                        },
                      ]}
                      value={state.selectOption}
                      closeOnSelect
                      printOptions="on-focus"
                      search
                      filterOptions={fuzzySearch}
                      placeholder={`${state.numSelect} ${t('selecionados')}`}
                      onChange={(value) => { setState({ selectOption: value }); }}
                    />
                  </div>
                </SearchInput>
              )
            }
          </Flex>
          <br />
          <TableContainer>
            <TableHead>
              <HeaderRow>
                {canEditEnvironments && (
                  <Box
                    backgroundColor={colors.brand.primary.base}
                    height={40}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    style={{
                      borderRadius: '10px 0 0 0',
                    }}
                  >
                    <Checkbox
                      checked={isChecked()}
                      onClick={() => (isChecked() ? notCheckAllItems() : checkAllItems())}
                    />
                  </Box>
                )}
                {
                  columnsEnvironment.map((column) => (
                    <HeaderCell key={column.value} onClick={() => handleSetOrderColumn(column)}>
                      {column.label}
                      {' '}
                      {state.orderColum === column.value ? chevron() : ''}
                    </HeaderCell>
                  ))
                }
                {canEditEnvironments && <HeaderCell />}
              </HeaderRow>
            </TableHead>
            <TableBody>
              {getDataPerPage.map((item) => (
                <Row key={`${item.DEV_ID}${item.ENVIRONMENT_ID}${item.ROOM_NAME}${item.UNIT_ID}`}>
                  {canEditEnvironments && (
                    <DataCell>
                      <Checkbox
                        checked={isCheckedItem(item)}
                        onClick={() => {
                          checkItem(item);
                        }}
                      />
                    </DataCell>
                  )}
                  <DataCell>{item.ROOM_NAME || item.DEV_ID || '-'}</DataCell>
                  <DataCell>{item.RTYPE_NAME || '-'}</DataCell>
                  <DataCell>{item.UNIT_NAME || '-'}</DataCell>
                  <DataCell>{item.COUNTRY_NAME || '-'}</DataCell>
                  <DataCell>{item.STATE_ID || '-'}</DataCell>
                  <DataCell>{item.CITY_NAME || '-'}</DataCell>
                  {(canEditEnvironments)
                    && (
                      <DataCell>
                        {canManage ? (
                          <EnvironmentDeleteButton item={item} onDelete={(env) => { setIsLoad(true); onDeleteClick(env); checkItem(env); setIsLoad(false); }} />
                        ) : null}
                        <ActionButton onClick={() => onEditClick(item)} variant="blue-inv">
                          <EditIcon color={colors.LightBlue} />
                        </ActionButton>
                      </DataCell>
                    )}
                </Row>
              ))}
            </TableBody>
          </TableContainer>
          <Pagination
            currentPage={state.currentPage}
            onNextPage={onNextPage}
            onPreviousPage={onPreviousPage}
            filteredRows={filteredRows}
            pageSize={pageSize}
          />
        </>
      )}
      {
        ((state.selectOption === 'setEnvironmentType') || (state.selectOption === 'deleteEnvironments')) && (
          <ModalEnvironment
            state={state}
            setEnvironments={setListEnvironments}
            setState={setState}
            t={t}
            render={render}
            roomTypes={roomTypes}
            listEnvironments={listEnvironments}
            updateOrDelete={updateOrDelete}
            selectedRType={state.selectedRType}
          />
        )
      }
    </>
  );
};

interface EnvironmentDeleteButtonProps {
  item: EnvironmentList;
  onDelete: (item: EnvironmentList) => void;
}

function EnvironmentDeleteButton({ item, onDelete }: Readonly<EnvironmentDeleteButtonProps>): ReactElement {
  if (!item.ENVIRONMENT_ID) {
    return (
      <ActionButton style={{ width: 35, padding: 5 }} variant="red-inv">
        <DeleteOutlineIcon colors="gray" />
      </ActionButton>
    );
  }
  return (
    <ActionButton onClick={() => onDelete(item)} variant="red-inv">
      <DeleteOutlineIcon colors={colors.Red} />
    </ActionButton>
  );
}

function ModalEnvironment({
  state,
  setState,
  roomTypes,
  listEnvironments,
  setEnvironments,
  render,
  t,
  updateOrDelete,
  selectedRType,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const title = state.selectOption === 'deleteEnvironments' ? t('deletarAmbientes') : t('definirTipoAmbiente');
  const send = state.selectOption === 'deleteEnvironments' ? t('deletar') : t('aplicar');
  return (
    <ModalWindow borderTop style={{ width: 400 }}>
      <h3>
        <strong>{title}</strong>
      </h3>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 10,
        padding: 20,
      }}
      >
        {
          state.selectOption !== 'deleteEnvironments' && (
            <>
              <SearchInput style={{ margin: 0, zIndex: 10 }}>
                <div style={{ width: '100%', paddingTop: 3, paddingBottom: 3 }}>
                  <Label>{t('multiplasConfiguracoes')}</Label>
                  <SelectSearch
                    options={roomTypes}
                    value={selectedRType}
                    printOptions="on-focus"
                    search
                    filterOptions={fuzzySearch}
                    placeholder={t('selecionarTipoAmbiente')}
                    // eslint-disable-next-line react/jsx-no-bind
                    onChange={(value) => setState({ selectedRType: value })}
                  />
                </div>
              </SearchInput>
              <p
                style={{
                  color: 'blue',
                  width: '100%',
                  textDecoration: 'underline',
                  paddingLeft: 3,
                  cursor: 'pointer',
                }}
                onClick={() => {
                  if (window.confirm(t('confirmaExcluirTipoAmbiente'))) {
                    setIsLoading(true);
                    setState({ selectedRType: null });
                    updateOrDelete('clear');
                    setIsLoading(false);
                  }
                }}
              >
                {t('limparAmbientes')}
              </p>
            </>
          )
        }
        {
          (state.selectOption === 'deleteEnvironments') && (
          <p
            style={{
              width: '100%',
              paddingLeft: 3,
            }}
          >
            {t('voceTemCertezaDeletarAmbientes')}
          </p>
          )
        }
        <Button variant={isLoading ? 'disabled' : 'primary'} onClick={() => { setIsLoading(true); updateOrDelete(''); setIsLoading(false); }}>{send}</Button>
        <p
          style={{ textDecoration: 'underline', color: '#6C6B6B', cursor: 'pointer' }}
          onClick={() => setState({ selectOption: '' })}
        >
          {t('cancelar')}
        </p>
      </div>
    </ModalWindow>
  );
}
